import React, { useCallback, useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";

import {
  fetchWisudaApproval,
  showWisudaApproval,
  approveWisuda,
  rejectWisuda,
  getWisudaDokumen,
} from "../../api";

import TableNew, {
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "../../components/layouts/TableNew";

import PageLoading from "../../components/pages/Loading";
import PageNoData from "../../components/pages/NoData";
import LayoutModal from "../../components/layouts/Modal";
import FormTextarea from "../../components/forms/Textarea";

import {
  saAlert,
  saConfirm,
  saToast,
} from "../../helpers";

import { titleState } from "../../storage/pages";


const WisudaApproval = () => {
  const setTitle = useSetRecoilState(titleState);

  const [isLoaded, setIsLoaded] = useState(false);

  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);

  const [detail, setDetail] = useState(null);
  const [detailModal, setDetailModal] = useState(false);

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectError, setRejectError] = useState("");

  const [documentUrl, setDocumentUrl] = useState(null);
  const [documentMimeType, setDocumentMimeType] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);

  const [processing, setProcessing] = useState(false);

  // =========================================================
  // SEARCH
  // =========================================================

  const [search, setSearch] = useState("");

  // =========================================================
  // PAGINATION
  // =========================================================

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);


  // =========================================================
  // TITLE
  // =========================================================

  useEffect(() => {
    setTitle("Verifikasi Pendaftaran Wisuda");
  }, [setTitle]);


  // =========================================================
  // FETCH DATA
  // =========================================================

  const fetchData = useCallback(async () => {
    try {
      setIsLoaded(false);

      const res = await fetchWisudaApproval();

      console.log("Wisuda Approval:", res.data);

      setItems(
        res?.data?.data ||
        res?.data?.approvals ||
        []
      );

      setRoles(
        res?.data?.roles ||
        []
      );

    } catch (err) {
      console.log("Error fetch approval:", err);

      saToast(
        "error",
        err?.response?.data?.message ||
          "Gagal mengambil data verifikasi wisuda."
      );

    } finally {
      setIsLoaded(true);
    }
  }, []);


  useEffect(() => {
    fetchData();
  }, [fetchData]);


  // =========================================================
  // RESET PAGINATION
  // =========================================================

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage]);


  // =========================================================
  // SEARCH & PAGINATION
  // =========================================================

  const filteredItems = items.filter((item) => {

    const mahasiswa =
      item?.pendaftaran?.mahasiswa;

    const nim = String(
      mahasiswa?.nim ||
      mahasiswa?.NIM ||
      ""
    ).toLowerCase();

    const nama = String(
      mahasiswa?.nama ||
      ""
    ).toLowerCase();

    const keyword =
      search
        .toLowerCase()
        .trim();

    if (!keyword) {
      return true;
    }

    return (
      nim.includes(keyword) ||
      nama.includes(keyword)
    );
  });


  const totalItems =
    filteredItems.length;


  const totalPages =
    Math.ceil(
      totalItems / itemsPerPage
    );


  const startIndex =
    (currentPage - 1) *
    itemsPerPage;


  const endIndex =
    startIndex +
    itemsPerPage;


  const paginatedItems =
    filteredItems.slice(
      startIndex,
      endIndex
    );


  // =========================================================
  // DETAIL
  // =========================================================

  const handleDetail = async (id) => {
    try {
      const res =
        await showWisudaApproval(id);

      console.log(
        "Detail approval:",
        res.data
      );

      setDetail(
        res?.data?.data ||
        res?.data?.approval ||
        res?.data
      );

      setDetailModal(true);

    } catch (err) {
      console.log(
        "Error detail:",
        err
      );

      saToast(
        "error",
        err?.response?.data?.message ||
          "Gagal mengambil detail verifikasi."
      );
    }
  };


  // =========================================================
  // CLOSE DETAIL
  // =========================================================

  const handleCloseDetail = () => {
    setDetailModal(false);
    setDetail(null);
  };


  // =========================================================
  // VIEW DOCUMENT
  // =========================================================

  const handleViewDocument = async (
    dokumenId
  ) => {

    if (!dokumenId) {

      saToast(
        "warning",
        "Dokumen tidak tersedia."
      );

      return;
    }

    try {

      setDocumentLoading(true);
      setDocumentModal(true);

      const res =
        await getWisudaDokumen(
          dokumenId
        );

      const mimeType =
        res?.headers?.[
          "content-type"
        ] ||
        "application/pdf";

      const blob =
        new Blob(
          [res.data],
          {
            type: mimeType,
          }
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      setDocumentUrl(url);
      setDocumentMimeType(
        mimeType
      );

    } catch (err) {

      console.log(
        "Error dokumen:",
        err
      );

      setDocumentModal(false);

      saToast(
        "error",
        err?.response?.data?.message ||
          "Dokumen tidak dapat dibuka."
      );

    } finally {

      setDocumentLoading(false);
    }
  };


  // =========================================================
  // CLOSE DOCUMENT
  // =========================================================

  const handleCloseDocument = () => {

    if (documentUrl) {

      window.URL.revokeObjectURL(
        documentUrl
      );
    }

    setDocumentUrl(null);
    setDocumentMimeType(null);
    setDocumentModal(false);
  };


  // =========================================================
  // APPROVE
  // =========================================================

  const handleApprove = () => {

    if (!detail?.id) {
      return;
    }

    saConfirm(
      "warning",
      "Yakin ingin menyetujui persyaratan ini?"
    ).then(
      async (result) => {

        if (!result.isConfirmed) {
          return;
        }

        try {

          setProcessing(true);

          await approveWisuda(
            detail.id
          );

          saAlert(
            "success",
            "Persyaratan berhasil disetujui."
          );

          handleCloseDetail();

          await fetchData();

        } catch (err) {

          console.log(
            "Error approve:",
            err
          );

          saToast(
            "error",
            err?.response?.data?.message ||
              "Gagal menyetujui persyaratan."
          );

        } finally {

          setProcessing(false);
        }
      }
    );
  };


  // =========================================================
  // OPEN REJECT
  // =========================================================

  const handleOpenReject = () => {

    setRejectNote("");
    setRejectError("");
    setRejectModal(true);
  };


  // =========================================================
  // CLOSE REJECT
  // =========================================================

  const handleCloseReject = () => {

    setRejectNote("");
    setRejectError("");
    setRejectModal(false);
  };


  // =========================================================
  // REJECT
  // =========================================================

  const handleReject = async (e) => {

    e.preventDefault();

    if (!detail?.id) {
      return;
    }

    if (!rejectNote.trim()) {

      setRejectError(
        "Catatan penolakan wajib diisi."
      );

      return;
    }

    try {

      setProcessing(true);

      await rejectWisuda(
        detail.id,
        {
          catatan:
            rejectNote,
        }
      );

      saAlert(
        "success",
        "Persyaratan ditolak dan dikembalikan untuk diperbaiki."
      );

      handleCloseReject();
      handleCloseDetail();

      await fetchData();

    } catch (err) {

      console.log(
        "Error reject:",
        err
      );

      if (
        err?.response?.status === 422
      ) {

        setRejectError(
          err?.response?.data?.message ||
            "Catatan penolakan wajib diisi."
        );

      } else {

        saToast(
          "error",
          err?.response?.data?.message ||
            "Gagal menolak persyaratan."
        );
      }

    } finally {

      setProcessing(false);
    }
  };


  // =========================================================
  // STATUS
  // =========================================================

  const getStatusLabel = (
    status
  ) => {

    switch (status) {

      case "pending":
        return "Menunggu Verifikasi";

      case "approved":
        return "Disetujui";

      case "rejected":
        return "Ditolak";

      default:
        return status || "-";
    }
  };


  const getStatusClass = (
    status
  ) => {

    switch (status) {

      case "approved":
        return "badge bg-success";

      case "rejected":
        return "badge bg-danger";

      case "pending":
        return "badge bg-warning text-dark";

      default:
        return "badge bg-secondary";
    }
  };


  // =========================================================
  // ROLE LABEL
  // =========================================================

  const getRoleLabel = (
    role
  ) => {

    switch (role) {

      case "akademik":
        return "Akademik";

      case "keuangan":
        return "Keuangan";

      default:
        return role || "-";
    }
  };


  // =========================================================
  // LOADING
  // =========================================================

  if (!isLoaded) {
    return <PageLoading />;
  }


  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {/* =====================================================
          HEADER
      ====================================================== */}

      <div className="row mb-3">

        <div className="col-md">

          <h5 className="font-weight-bold mb-1">
            Verifikasi Pendaftaran Wisuda
          </h5>

          <small className="text-muted">
            Daftar persyaratan pendaftaran
            wisuda yang menunggu verifikasi.
          </small>

        </div>


        <div className="col-md-auto">

          {roles?.length > 0 &&
            roles.map((role) => (

              <span
                key={role}
                className="badge bg-primary mr-1"
              >
                {getRoleLabel(role)}
              </span>

            ))
          }

        </div>

      </div>


      {/* =====================================================
          SEARCH & PAGE SIZE
      ====================================================== */}

      <div className="row mb-3">

        <div className="col-md-6">

          <div className="input-group">

            <input
              type="text"
              className="form-control"
              placeholder="Cari berdasarkan NIM atau nama mahasiswa..."
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

            {search && (

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  setSearch("")
                }
              >
                <i className="fa fa-times" />
              </button>

            )}

          </div>

        </div>


        <div className="col-md-6 text-right">

          <div className="d-inline-flex align-items-center">

            <span className="mr-2">
              Tampilkan
            </span>

            <select
              className="form-control form-control-sm"
              value={itemsPerPage}
              onChange={(e) =>
                setItemsPerPage(
                  Number(
                    e.target.value
                  )
                )
              }
              style={{
                width: "80px",
              }}
            >

              <option value={10}>
                10
              </option>

              <option value={25}>
                25
              </option>

              <option value={50}>
                50
              </option>

              <option value={100}>
                100
              </option>

            </select>

            <span className="ml-2">
              data
            </span>

          </div>

        </div>

      </div>


      {/* =====================================================
          SEARCH RESULT INFO
      ====================================================== */}

      {search && (

        <div className="mb-2">

          <small className="text-muted">

            Menampilkan hasil pencarian untuk:

            {" "}

            <strong>
              {search}
            </strong>

            {" "}
            ({totalItems} data)

          </small>

        </div>

      )}


      {/* =====================================================
          TABLE
      ====================================================== */}

      {paginatedItems?.length > 0 ? (

        <TableNew>

          <Thead>

            <Tr className="text-center">

              <Th>No</Th>

              <Th>
                No. Pendaftaran
              </Th>

              <Th>
                NIM
              </Th>

              <Th>
                Nama Mahasiswa
              </Th>

              <Th>
                Persyaratan
              </Th>

              <Th>
                Role
              </Th>

              <Th>
                Status
              </Th>

              <Th>
                Aksi
              </Th>

            </Tr>

          </Thead>


          <Tbody>

            {paginatedItems.map(
              (item, index) => {

                const pendaftaran =
                  item?.pendaftaran;

                const mahasiswa =
                  pendaftaran
                    ?.mahasiswa;

                const persyaratan =
                  item?.persyaratan;


                return (

                  <Tr
                    key={item.id}
                  >

                    <Td className="text-center">

                      {startIndex +
                        index +
                        1}

                    </Td>


                    <Td>

                      {pendaftaran
                        ?.nomor_pendaftaran ||
                        "-"}

                    </Td>


                    <Td>

                      {mahasiswa?.nim ||
                        mahasiswa?.NIM ||
                        "-"}

                    </Td>


                    <Td>

                      {mahasiswa?.nama ||
                        "-"}

                    </Td>


                    <Td>

                      <strong>

                        {persyaratan
                          ?.nama ||
                          "-"}

                      </strong>


                      {persyaratan
                        ?.deskripsi && (

                        <div>

                          <small className="text-muted">

                            {
                              persyaratan
                                .deskripsi
                            }

                          </small>

                        </div>

                      )}

                    </Td>


                    <Td className="text-center">

                      <span className="badge bg-info">

                        {getRoleLabel(
                          item
                            ?.approval_role
                        )}

                      </span>

                    </Td>


                    <Td className="text-center">

                      <span
                        className={getStatusClass(
                          item?.status
                        )}
                      >

                        {getStatusLabel(
                          item?.status
                        )}

                      </span>

                    </Td>


                    <Td className="text-center">

                      <button
                        type="button"
                        className="btn btn-sm btn-primary py-0 px-2"
                        onClick={() =>
                          handleDetail(
                            item.id
                          )
                        }
                      >

                        <i className="fa fa-fw fa-eye" />

                        &nbsp;

                        Detail

                      </button>

                    </Td>

                  </Tr>

                );
              }
            )}

          </Tbody>

        </TableNew>

      ) : (

        <PageNoData />

      )}


      {/* =====================================================
          PAGINATION INFO
      ====================================================== */}

      {totalItems > 0 && (

        <div className="row mt-3">

          <div className="col-md-6">

            <small className="text-muted">

              Menampilkan{" "}

              {startIndex + 1}

              {" - "}

              {Math.min(
                endIndex,
                totalItems
              )}

              {" dari "}

              {totalItems}

              {" data"}

            </small>

          </div>


          {/* =================================================
              PAGINATION
          ================================================== */}

          <div className="col-md-6">

            {totalPages > 1 && (

              <div className="d-flex justify-content-end">

                <nav>

                  <ul className="pagination pagination-sm mb-0">


                    {/* PREVIOUS */}

                    <li
                      className={`page-item ${
                        currentPage === 1
                          ? "disabled"
                          : ""
                      }`}
                    >

                      <button
                        type="button"
                        className="page-link"
                        disabled={
                          currentPage === 1
                        }
                        onClick={() =>
                          setCurrentPage(
                            (prev) =>
                              Math.max(
                                prev - 1,
                                1
                              )
                          )
                        }
                      >
                        &laquo;
                      </button>

                    </li>


                    {/* FIRST PAGE */}

                    <li
                      className={`page-item ${
                        currentPage === 1
                          ? "active"
                          : ""
                      }`}
                    >

                      <button
                        type="button"
                        className="page-link"
                        onClick={() =>
                          setCurrentPage(1)
                        }
                      >
                        1
                      </button>

                    </li>


                    {/* LEFT ELLIPSIS */}

                    {currentPage > 4 && (

                      <li className="page-item disabled">

                        <span className="page-link">
                          ...
                        </span>

                      </li>

                    )}


                    {/* PAGE AROUND CURRENT */}

                    {Array.from(
                      {
                        length:
                          totalPages,
                      },
                      (_, index) =>
                        index + 1
                    )
                      .filter((page) => {

                        if (
                          page === 1
                        ) {
                          return false;
                        }

                        if (
                          page ===
                          totalPages
                        ) {
                          return false;
                        }

                        return (
                          page >=
                            currentPage -
                              2 &&
                          page <=
                            currentPage +
                              2
                        );
                      })
                      .map((page) => (

                        <li
                          key={page}
                          className={`page-item ${
                            currentPage ===
                            page
                              ? "active"
                              : ""
                          }`}
                        >

                          <button
                            type="button"
                            className="page-link"
                            onClick={() =>
                              setCurrentPage(
                                page
                              )
                            }
                          >
                            {page}
                          </button>

                        </li>

                      ))}


                    {/* RIGHT ELLIPSIS */}

                    {currentPage <
                      totalPages -
                        3 && (

                      <li className="page-item disabled">

                        <span className="page-link">
                          ...
                        </span>

                      </li>

                    )}


                    {/* LAST PAGE */}

                    {totalPages > 1 && (

                      <li
                        className={`page-item ${
                          currentPage ===
                          totalPages
                            ? "active"
                            : ""
                        }`}
                      >

                        <button
                          type="button"
                          className="page-link"
                          onClick={() =>
                            setCurrentPage(
                              totalPages
                            )
                          }
                        >
                          {totalPages}
                        </button>

                      </li>

                    )}


                    {/* NEXT */}

                    <li
                      className={`page-item ${
                        currentPage ===
                        totalPages
                          ? "disabled"
                          : ""
                      }`}
                    >

                      <button
                        type="button"
                        className="page-link"
                        disabled={
                          currentPage ===
                          totalPages
                        }
                        onClick={() =>
                          setCurrentPage(
                            (prev) =>
                              Math.min(
                                prev + 1,
                                totalPages
                              )
                          )
                        }
                      >
                        &raquo;
                      </button>

                    </li>

                  </ul>

                </nav>

              </div>

            )}

          </div>

        </div>

      )}


      {/* =====================================================
          DETAIL MODAL
      ====================================================== */}

      <LayoutModal
        title="Detail Verifikasi Wisuda"
        modal={detailModal}
        onClose={
          handleCloseDetail
        }
      >

        {detail && (

          <>

            {/* MAHASISWA */}

            <div className="row">

              <div className="col-md-6">

                <label className="font-weight-bold">
                  No. Pendaftaran
                </label>

                <div>

                  {detail?.pendaftaran
                    ?.nomor_pendaftaran ||
                    "-"}

                </div>

              </div>


              <div className="col-md-6">

                <label className="font-weight-bold">
                  Status Pendaftaran
                </label>

                <div>

                  <span
                    className={
                      detail?.pendaftaran
                        ?.status ===
                      "revision"
                        ? "badge bg-danger"
                        : "badge bg-warning text-dark"
                    }
                  >

                    {detail?.pendaftaran
                      ?.status ||
                      "-"}

                  </span>

                </div>

              </div>

            </div>


            <hr />


            <div className="row">

              <div className="col-md-4">

                <label className="font-weight-bold">
                  NIM
                </label>

                <div>

                  {detail?.pendaftaran
                    ?.mahasiswa?.nim ||
                    detail?.pendaftaran
                      ?.mahasiswa?.NIM ||
                    "-"}

                </div>

              </div>


              <div className="col-md-8">

                <label className="font-weight-bold">
                  Nama Mahasiswa
                </label>

                <div>

                  {detail?.pendaftaran
                    ?.mahasiswa?.nama ||
                    "-"}

                </div>

              </div>

            </div>


            <hr />


            {/* PERSYARATAN */}

            <div>

              <label className="font-weight-bold">
                Persyaratan
              </label>

              <div>

                {detail
                  ?.persyaratan
                  ?.nama ||
                  "-"}

              </div>

            </div>


            <div className="mt-2">

              <label className="font-weight-bold">
                Role Verifikasi
              </label>

              <div>

                {getRoleLabel(
                  detail
                    ?.approval_role
                )}

              </div>

            </div>


            <div className="mt-2">

              <label className="font-weight-bold">
                Status
              </label>

              <div>

                <span
                  className={getStatusClass(
                    detail?.status
                  )}
                >

                  {getStatusLabel(
                    detail?.status
                  )}

                </span>

              </div>

            </div>


            {/* DOKUMEN */}

            {detail?.dokumen && (

              <div className="mt-3 p-3 border rounded">

                <div className="font-weight-bold mb-2">
                  Dokumen
                </div>


                <div className="mb-2">

                  <small>

                    {detail.dokumen
                      ?.nama_file_original ||
                      detail.dokumen
                        ?.nama_file ||
                      "-"}

                  </small>

                </div>


                <button
                  type="button"
                  className="btn btn-sm btn-info"
                  onClick={() =>
                    handleViewDocument(
                      detail
                        .dokumen
                        .id
                    )
                  }
                >

                  <i className="fa fa-fw fa-file-pdf" />

                  &nbsp;

                  Lihat Dokumen

                </button>

              </div>

            )}


            {/* CATATAN */}

            {detail?.catatan && (

              <div className="mt-3">

                <label className="font-weight-bold">
                  Catatan
                </label>

                <div className="alert alert-warning">

                  {detail.catatan}

                </div>

              </div>

            )}


            {/* ACTION */}

            {detail?.status ===
              "pending" && (

              <>

                <hr />

                <div className="btn-group">

                  {/* APPROVE */}

                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    disabled={
                      processing
                    }
                    onClick={
                      handleApprove
                    }
                  >

                    <i className="fa fa-fw fa-check" />

                    &nbsp;

                    Setujui

                  </button>


                  {/* REJECT */}

                  {![
                    "bebas_administrasi",
                    "sertifikat_kompetensi",
                  ].includes(
                    detail
                      ?.persyaratan
                      ?.kode
                  ) && (

                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      disabled={
                        processing
                      }
                      onClick={
                        handleOpenReject
                      }
                    >

                      <i className="fa fa-fw fa-times" />

                      &nbsp;

                      Tolak

                    </button>

                  )}


                  {/* CLOSE */}

                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={
                      processing
                    }
                    onClick={
                      handleCloseDetail
                    }
                  >

                    Tutup

                  </button>

                </div>

              </>

            )}

          </>

        )}

      </LayoutModal>


      {/* =====================================================
          REJECT MODAL
      ====================================================== */}

      <LayoutModal
        title="Tolak Persyaratan"
        modal={rejectModal}
        onClose={
          handleCloseReject
        }
      >

        <form
          onSubmit={
            handleReject
          }
        >

          <div className="alert alert-warning">

            Silakan masukkan alasan/catatan
            penolakan agar mahasiswa mengetahui
            bagian yang harus diperbaiki.

          </div>


          <FormTextarea
            label="Catatan Penolakan"
            name="catatan"
            value={
              rejectNote
            }
            onChange={(e) => {

              setRejectNote(
                e.target.value
              );

              setRejectError(
                ""
              );

            }}
            error={
              rejectError
            }
          />


          <div className="btn-group">

            <button
              type="submit"
              className="btn btn-sm btn-danger"
              disabled={
                processing
              }
            >

              <i className="fa fa-fw fa-times" />

              &nbsp;

              Tolak

            </button>


            <button
              type="button"
              className="btn btn-sm btn-secondary"
              disabled={
                processing
              }
              onClick={
                handleCloseReject
              }
            >

              Batal

            </button>

          </div>

        </form>

      </LayoutModal>


      {/* =====================================================
          DOCUMENT MODAL
      ====================================================== */}

      <LayoutModal
        title="Dokumen Persyaratan Wisuda"
        modal={
          documentModal
        }
        onClose={
          handleCloseDocument
        }
      >

        {documentLoading ? (

          <div className="text-center p-4">

            <i className="fa fa-spinner fa-spin" />

            &nbsp;

            Memuat dokumen...

          </div>

        ) : documentUrl ? (

          documentMimeType?.startsWith(
            "image/"
          ) ? (

            <div className="text-center">

              <img
                src={
                  documentUrl
                }
                alt="Dokumen"
                style={{
                  maxWidth:
                    "100%",
                  maxHeight:
                    "70vh",
                }}
              />

            </div>

          ) : (

            <iframe
              src={
                documentUrl
              }
              title="Dokumen Wisuda"
              style={{
                width:
                  "100%",
                height:
                  "70vh",
                border:
                  "none",
              }}
            />

          )

        ) : (

          <div className="text-center text-muted">

            Dokumen tidak tersedia.

          </div>

        )}

      </LayoutModal>

    </>
  );
};


export default WisudaApproval;